import { useEffect, useState, useMemo } from 'react';
import '../../CreateMarket.css';
import { Connection, Keypair, PublicKey } from '@solana/web3.js';
import { Market } from '@project-serum/serum';
import { EVENT_QUEUE_HEADER_SIZE, EVENT_SIZE, ORDERBOOK_HEADER_SIZE, ORDERBOOK_NODE_SIZE, REQUEST_QUEUE_HEADER_SIZE, REQUEST_SIZE } from '../../utils/serum';
import { useOpenbook } from '../../hooks/useOpenbook';
import { openbookProgramId } from '../../utils/constants';
import { encode } from 'bs58';
import { useJito } from '../../hooks/useJito';
import { createOpenbookMarketJitoTip } from '../../../ConfigFees';

export function CreateMarket({
    connection,
    wallet,
    walletLoaded,
    deployedToken,
    setDeployedMarketId,
    logMessage,
}: {
    connection: Connection;
    wallet: Keypair;
    walletLoaded: boolean;
    deployedToken: string;
    setDeployedMarketId: React.Dispatch<React.SetStateAction<string | undefined>>;
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const { getCreateOpenbookMarketTransactions } = useOpenbook();
    const { spamAndConfirmBundle } = useJito();

    const [createMarketBaseMint, setCreateMarketBaseMint] = useState<string>(deployedToken);
    const [createMarketQuoteMint, setCreateMarketQuoteMint] = useState<string>('So11111111111111111111111111111111111111112');

    const [minOrderSize, setMinOrderSize] = useState<number>(1);
    const [priceTick, setPriceTick] = useState<number>(0);

    const [createMarketAdvancedSettingsStatus, setCreateMarketAdvancedSettingsStatus] = useState<boolean>(true);

    const [eventQueueLength, setEventQueueLength] = useState<number>(128);
    const [eventQueueLengthBytes, setEventQueueLengthBytes] = useState(calculateTotalAccountSize(eventQueueLength, EVENT_QUEUE_HEADER_SIZE, EVENT_SIZE));
    const [requestQueueLength, setRequestQueueLength] = useState<number>(1);
    const [requestQueueLengthBytes, setRequestQueueLengthBytes] = useState(
        calculateTotalAccountSize(requestQueueLength, REQUEST_QUEUE_HEADER_SIZE, REQUEST_SIZE),
    );
    const [orderbookLength, setOrderbookLength] = useState<number>(201);
    const [orderbookLengthBytes, setOrderbookLengthBytes] = useState(calculateTotalAccountSize(orderbookLength, ORDERBOOK_HEADER_SIZE, ORDERBOOK_NODE_SIZE));

    const [totalRentEstimate, setTotalRentEstimate] = useState<number>(0);

    const [deploymentStarted, setDeploymentStarted] = useState<boolean>(false);

    const handleStringChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
    };

    const handleNumberChange = (setter: React.Dispatch<React.SetStateAction<number>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setter(Number(event.target.value));
    };

    const handleBooleanChange = (setter: React.Dispatch<React.SetStateAction<boolean>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setter(event.target.checked);
    };

    const handleCustomSizes =
        (
            lengthSetter: React.Dispatch<React.SetStateAction<number>>,
            bytesSetter: React.Dispatch<React.SetStateAction<number>>,
            calculateParams: [number, number],
        ) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = Number(event.target.value);

            lengthSetter(newValue);

            const newSizeInBytes = calculateTotalAccountSize(newValue, ...calculateParams);
            bytesSetter(newSizeInBytes);
        };

    const eventQueueLengthChange = useMemo(
        () => handleCustomSizes(setEventQueueLength, setEventQueueLengthBytes, [EVENT_QUEUE_HEADER_SIZE, EVENT_SIZE]),
        [setEventQueueLength],
    );
    const requestQueueLengthChange = useMemo(
        () => handleCustomSizes(setRequestQueueLength, setRequestQueueLengthBytes, [REQUEST_QUEUE_HEADER_SIZE, REQUEST_SIZE]),
        [setRequestQueueLength],
    );
    const orderbookLengthChange = useMemo(
        () => handleCustomSizes(setOrderbookLength, setOrderbookLengthBytes, [ORDERBOOK_HEADER_SIZE, ORDERBOOK_NODE_SIZE]),
        [setOrderbookLength],
    );

    const createMarketBaseMintChange = handleStringChange(setCreateMarketBaseMint);
    const createMarketQuoteMintChange = handleStringChange(setCreateMarketQuoteMint);

    const minOrderSizeChange = handleNumberChange(setMinOrderSize);
    const priceTickChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = Math.min(Number(event.target.value), 0.000001);
        setPriceTick(inputValue);
    };

    const createMarketAdvancedSettingsStatusChange = handleBooleanChange(setCreateMarketAdvancedSettingsStatus);
    function calculateTotalAccountSize(individualAccountSize: number, accountHeaderSize: number, length: number) {
        const accountPadding = 12;
        const minRequiredSize = accountPadding + accountHeaderSize + length * individualAccountSize;

        const modulo = minRequiredSize % 8;

        return modulo <= 4 ? minRequiredSize + (4 - modulo) : minRequiredSize + (8 - modulo + 4);
    }

    useEffect(() => {
        async function calculateTotalRent() {
            const [marketAccountRent, eventQueueRent, requestQueueRent, orderbookRent] = await Promise.all([
                connection.getMinimumBalanceForRentExemption(Market.getLayout(new PublicKey(openbookProgramId)).span),
                connection.getMinimumBalanceForRentExemption(eventQueueLengthBytes),
                connection.getMinimumBalanceForRentExemption(requestQueueLengthBytes),
                connection.getMinimumBalanceForRentExemption(orderbookLengthBytes),
            ]);

            setTotalRentEstimate(parseFloat(((marketAccountRent + eventQueueRent + requestQueueRent + 2 * orderbookRent) / 1e9).toFixed(2)));
        }

        calculateTotalRent();
    }, [eventQueueLength, orderbookLength, requestQueueLength]);

    async function createMarket(
        wallet: Keypair,
        baseMint: string | PublicKey,
        quoteMint: string | PublicKey,
        lotSize: number,
        tickSize: number,
        totalEventQueueSize: number,
        totalRequestQueueSize: number,
        totalOrderbookSize: number,
    ) {
        if (!walletLoaded) {
            logMessage('Wallet not loaded');
            return;
        }
        if (deploymentStarted) {
            logMessage('Deployment already started');
            return;
        }

        logMessage('Starting deployment');
        setDeploymentStarted(true);

        const { vaultTransaction, marketTransaction, marketId } = await getCreateOpenbookMarketTransactions(
            connection,
            wallet,
            baseMint,
            quoteMint,
            lotSize,
            tickSize,
            totalEventQueueSize,
            totalRequestQueueSize,
            totalOrderbookSize,
            new PublicKey(openbookProgramId),
        );

        await spamAndConfirmBundle(connection, [vaultTransaction, marketTransaction], wallet, createOpenbookMarketJitoTip);

        logMessage(`The Market has been successufully created. The liquidity pair can now be deployed. Market ID: ${marketId}.`, 'success');

        const vaultSignature = encode(vaultTransaction.signatures[0]);
        const marketSignature = encode(marketTransaction.signatures[0]);

        logMessage(`Bundle sent`);
        logMessage(`Vault signature: ${vaultSignature}`);
        logMessage(`Market signature: ${marketSignature}`);

        setDeployedMarketId(marketId);
    }

    return (
        <section>
            <h2>Create Market</h2>
            <section>
                <div className='create-market-section'>
                    <div className='create-market-section--left'>
                        <h3>Mints</h3>
                        <p>Configure the mints for the tokens you want to create a market for.</p>
                    </div>
                    <div className='create-market-section--right'>
                        <div>
                            <p>Base Mint</p>
                            <input type='text' value={createMarketBaseMint} onChange={createMarketBaseMintChange} />
                        </div>
                        <div>
                            <p>Quote Mint</p>
                            <input type='text' value={createMarketQuoteMint} onChange={createMarketQuoteMintChange} />
                        </div>
                    </div>
                </div>
                <div className='create-market-section'>
                    <div className='create-market-section--left'>
                        <h3>Tickers</h3>
                        <p>Configure the tick sizes, or lowest representable quantities of base and quote tokens.</p>
                    </div>
                    <div className='create-market-section--right'>
                        <div>
                            <p>Min. Order Size &nbsp; &#8212; &nbsp; 1 (regular)</p>
                            <input type='number' value={minOrderSize} onChange={minOrderSizeChange} />
                        </div>
                        <div>
                            <p>Price Tick &nbsp; &#8212; &nbsp; 0.000001 (max)</p>
                            <input type='number' min={0} value={priceTick} onChange={priceTickChange} />
                        </div>
                    </div>
                </div>
                <div className='create-market-section'>
                    <div className='create-market-section--left'>
                        <h3>Advanced Options</h3>
                        <p>Configure sizes for the different accounts used to create the market to adjust rent cost.</p>
                        <div className='total-rent-estimate'>
                            <p>Total Rent Estimate</p>
                            <p className='total-rent-estimate--accent'>
                                {totalRentEstimate} <span>SOL</span>
                            </p>
                        </div>
                    </div>
                    <div className='create-market-section--right'>
                        <p>Use Advanced Options</p>
                        <div className='create-market-advanced-settings'>
                            <p>Set custom sizes for market accounts.</p>
                            <label className='switch'>
                                <input type='checkbox' checked={createMarketAdvancedSettingsStatus} onChange={createMarketAdvancedSettingsStatusChange} />
                                <span className='slider round'></span>
                            </label>
                        </div>
                        <div className='create-market-advanced-settings-options'>
                            <p>Event Queue Length</p>
                            <input type='number' value={eventQueueLength} onChange={eventQueueLengthChange} disabled={!createMarketAdvancedSettingsStatus} />
                            <span>{eventQueueLengthBytes} Bytes</span>
                        </div>
                        <div className='create-market-advanced-settings-options'>
                            <p>Request Queue Length</p>
                            <input
                                type='number'
                                value={requestQueueLength}
                                onChange={requestQueueLengthChange}
                                disabled={!createMarketAdvancedSettingsStatus}
                            />
                            <span>{requestQueueLengthBytes} Bytes</span>
                        </div>
                        <div className='create-market-advanced-settings-options'>
                            <p>Orderbook Length</p>
                            <input type='number' value={orderbookLength} onChange={orderbookLengthChange} disabled={!createMarketAdvancedSettingsStatus} />
                            <span>{orderbookLengthBytes} Bytes</span>
                        </div>
                    </div>
                </div>
                <button
                    className='button-accent button-250'
                    onClick={() =>
                        createMarket(
                            wallet,
                            createMarketBaseMint,
                            createMarketQuoteMint,
                            minOrderSize,
                            priceTick,
                            eventQueueLengthBytes,
                            requestQueueLengthBytes,
                            orderbookLengthBytes,
                        )
                    }
                >
                    Create Market
                </button>
            </section>
        </section>
    );
}
